import React, { useEffect } from 'react';
import {
  ProductsTable,
  SidebarWithHeader,
  CreateNewProductModal,
} from '../../components/admin';
import { MdRefresh } from 'react-icons/md';
import { useAdminProductStore } from '../../stores';
import { toast } from 'react-toastify';

function ProductsPage() {
  const {
    products,
    products_loading: loading,
    products_error: error,
    fetchProducts,
    exportProductsToExcel,
  } = useAdminProductStore();

  const [exporting, setExporting] = React.useState(false);
  const [search, setSearch] = React.useState('');

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleRefresh = async () => {
    await fetchProducts(true);
  };

  // By name or category — this is the page titled "Inventory Archive" in its
  // own header, so it's what gets searched when someone asks for search "in
  // inventory". The separate /admin/inventory stock-levels page already has
  // its own search; this was the one missing it.
  const needle = search.trim().toLowerCase();
  const filteredProducts = needle
    ? products.filter((p) =>
        String(p.name || '').toLowerCase().includes(needle) ||
        String(p.category || '').toLowerCase().includes(needle) ||
        String(p.subCategory || '').toLowerCase().includes(needle)
      )
    : products;

  const handleExport = async () => {
    setExporting(true);
    const res = await exportProductsToExcel();
    setExporting(false);
    if (!res.success) toast.error(res.message);
  };

  return (
    <SidebarWithHeader>
      <div className="flex flex-col gap-6 mb-8">
        <div className="flex justify-between items-center bg-white p-6 rounded-lg border border-bronze/10">
          <div>
            <h1 className="text-3xl font-editorial font-black text-bronze uppercase tracking-widest">Inventory Archive</h1>
            <p className="text-[10px] text-bronze/40 mt-2 font-bold uppercase tracking-[0.3em]">Manage Heritage Products & Stock Levels</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-3 px-6 py-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-lg hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 active:scale-95 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {exporting ? 'Exporting...' : 'Mass Export'}
            </button>
            <CreateNewProductModal />
            <button
              onClick={handleRefresh}
              className="flex items-center gap-3 px-6 py-3 bg-white border border-bronze/20 text-bronze text-[10px] font-black uppercase tracking-[0.2em] rounded-lg hover:bg-bronze/5 transition-all active:scale-95"
            >
              <MdRefresh className="text-lg" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-bronze/10 rounded-lg px-5 py-4 mb-6 flex items-center gap-4">
        <span className="material-symbols-outlined text-bronze/30">search</span>
        <input
          className="flex-1 text-sm text-bronze bg-transparent focus:outline-none placeholder:text-bronze/30"
          placeholder="Search by product name or category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-bronze/50 hover:text-bronze transition-colors"
          >
            <span className="material-symbols-outlined text-sm">close</span>
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-bronze/20 border-t-gold rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-lg font-editorial text-red-500">There was an error loading products</p>
        </div>
      ) : (
        <>
          {search && (
            <p className="text-[10px] text-bronze/40 font-bold uppercase tracking-widest mb-3">
              {filteredProducts.length} of {products.length} products match "{search}"
            </p>
          )}
          <ProductsTable products={filteredProducts} />
        </>
      )}
    </SidebarWithHeader>
  );
}

export default ProductsPage;
